/**
 * Auth state via Zustand + Amplify hub.
 *
 * Exposes the current user + tenant + role, plus convenience methods
 * for sign-up / sign-in / confirm / sign-out. All credential I/O goes
 * through Amplify's `@aws-amplify/auth` API; we layer on a profile
 * fetch from `GET /v1/auth/me` to hydrate the role and full profile.
 */

import { useEffect } from "react";
import { create } from "zustand";
import {
  confirmResetPassword,
  confirmSignIn,
  confirmSignUp,
  resetPassword,
  signIn,
  signOut as amplifySignOut,
  signUp,
  fetchUserAttributes,
} from "@aws-amplify/auth";
import { Hub } from "aws-amplify/utils";

import { getAuthMe, postAuthSignout } from "@starz/shared/api/sdk.gen";
import type { AuthMe, Role } from "@starz/shared/api/types.gen";
import { parseSubdomain } from "@/lib/tenant/resolver";

type AuthStatus = "unknown" | "anonymous" | "authenticated" | "mfa_required";

interface AuthState {
  status: AuthStatus;
  user: AuthMe | null;
  error: string | null;
  // Setters used internally by the actions and hub listener
  setStatus: (status: AuthStatus) => void;
  setUser: (user: AuthMe | null) => void;
  setError: (err: string | null) => void;
}

export const useAuthStore = create<AuthState>((set) => ({
  status: "unknown",
  user: null,
  error: null,
  setStatus: (status) => set({ status }),
  setUser: (user) => set({ user }),
  setError: (error) => set({ error }),
}));

/** Hydrate the user profile from /v1/auth/me. */
async function refreshProfile(): Promise<void> {
  const result = await getAuthMe();
  if ("data" in result) {
    useAuthStore.getState().setUser(result.data);
    useAuthStore.getState().setStatus("authenticated");
  } else {
    useAuthStore.getState().setUser(null);
    useAuthStore.getState().setStatus("anonymous");
  }
}

/**
 * One-time hook installed at the app shell. Listens to Amplify's auth
 * hub for sign-in / sign-out events and refreshes the profile.
 */
export function useAuthInit(): void {
  useEffect(() => {
    const setStatus = useAuthStore.getState().setStatus;

    // Initial hydration: try fetching the current user attributes
    fetchUserAttributes()
      .then(() => refreshProfile())
      .catch(() => setStatus("anonymous"));

    const unsubscribe = Hub.listen("auth", ({ payload }) => {
      switch (payload.event) {
        case "signedIn":
          void refreshProfile();
          break;
        case "signedOut":
          useAuthStore.getState().setUser(null);
          setStatus("anonymous");
          break;
        case "tokenRefresh_failure":
          useAuthStore.getState().setUser(null);
          setStatus("anonymous");
          break;
        default:
          break;
      }
    });
    return () => unsubscribe();
  }, []);
}

// ---------- actions ----------

export async function doSignUp(input: {
  email: string;
  password: string;
  first_name: string;
  last_name: string;
}): Promise<{ ok: boolean; error?: string; needsConfirm?: boolean }> {
  try {
    const host = window.location.host;
    // Cognito only persists user attributes that the client explicitly
    // sets at signUp time; PreSignUp triggers cannot modify them.
    // Pass `custom:tenant_id` (resolved from the subdomain) and
    // `custom:role` so they end up on the Cognito user record and in
    // the ID token. The PreSignUp trigger validates them against the
    // host-resolved tenant before Cognito creates the account.
    const tenantId = parseSubdomain(host) ?? "";
    if (!tenantId) {
      return {
        ok: false,
        error: "Could not resolve tenant from the host. Try a tenant subdomain.",
      };
    }
    const result = await signUp({
      username: input.email,
      password: input.password,
      options: {
        userAttributes: {
          email: input.email,
          given_name: input.first_name,
          family_name: input.last_name,
          "custom:tenant_id": tenantId,
          "custom:role": "customer",
        },
        clientMetadata: { host },
      },
    });
    return { ok: true, needsConfirm: !result.isSignUpComplete };
  } catch (err) {
    const message = err instanceof Error ? err.message : "Signup failed";
    return { ok: false, error: message };
  }
}

export async function doConfirmSignUp(
  email: string,
  code: string,
): Promise<{ ok: boolean; error?: string }> {
  try {
    await confirmSignUp({ username: email, confirmationCode: code });
    return { ok: true };
  } catch (err) {
    const message = err instanceof Error ? err.message : "Confirmation failed";
    return { ok: false, error: message };
  }
}

export async function doSignIn(input: {
  email: string;
  password: string;
}): Promise<{ ok: boolean; mfaRequired?: boolean; error?: string }> {
  try {
    // Clear any cached session before attempting sign-in. Guards
    // against `UserAlreadyAuthenticatedException` when localStorage
    // has stale tokens — typical after a deleted Cognito user, an
    // expired refresh token, or a previous broken signup. The
    // /signin page is only ever hit by anonymous users
    // (ProtectedRoute redirects authenticated users away), so
    // signing out here is always safe.
    try {
      await amplifySignOut({ global: false });
    } catch {
      // No active session; that's fine.
    }

    const result = await signIn({
      username: input.email,
      password: input.password,
      options: { authFlowType: "USER_SRP_AUTH" },
    });
    if (result.nextStep.signInStep === "DONE") {
      await refreshProfile();
      return { ok: true };
    }
    if (
      result.nextStep.signInStep === "CONFIRM_SIGN_IN_WITH_TOTP_CODE" ||
      result.nextStep.signInStep === "CONFIRM_SIGN_IN_WITH_SMS_CODE"
    ) {
      useAuthStore.getState().setStatus("mfa_required");
      return { ok: true, mfaRequired: true };
    }
    return { ok: false, error: `Unexpected next step: ${result.nextStep.signInStep}` };
  } catch (err) {
    const message = err instanceof Error ? err.message : "Sign-in failed";
    return { ok: false, error: message };
  }
}

export async function doConfirmMfa(code: string): Promise<{ ok: boolean; error?: string }> {
  try {
    const result = await confirmSignIn({ challengeResponse: code });
    if (result.nextStep.signInStep === "DONE") {
      await refreshProfile();
      return { ok: true };
    }
    return { ok: false, error: "MFA challenge did not complete" };
  } catch (err) {
    const message = err instanceof Error ? err.message : "MFA failed";
    return { ok: false, error: message };
  }
}

/**
 * Start a forgot-password flow. Cognito emails a confirmation code to
 * the address on file. Returns ok even in the (rare) case Cognito
 * reports the reset is already complete. Amplify does not reveal whether
 * the email exists, so this is safe against user enumeration.
 */
export async function doResetPassword(email: string): Promise<{ ok: boolean; error?: string }> {
  try {
    await resetPassword({ username: email });
    return { ok: true };
  } catch (err) {
    const message = err instanceof Error ? err.message : "Could not start the password reset";
    return { ok: false, error: message };
  }
}

/** Complete a forgot-password flow with the emailed code + a new password. */
export async function doConfirmResetPassword(input: {
  email: string;
  code: string;
  newPassword: string;
}): Promise<{ ok: boolean; error?: string }> {
  try {
    await confirmResetPassword({
      username: input.email,
      confirmationCode: input.code,
      newPassword: input.newPassword,
    });
    return { ok: true };
  } catch (err) {
    const message = err instanceof Error ? err.message : "Could not reset your password";
    return { ok: false, error: message };
  }
}

export async function doSignOut(): Promise<void> {
  // Inform the API (audit log + admin-global-sign-out) and then clear
  // local Amplify state.
  try {
    await postAuthSignout();
  } catch {
    // Swallow — local sign-out should still proceed.
  }
  await amplifySignOut();
  useAuthStore.getState().setUser(null);
  useAuthStore.getState().setStatus("anonymous");
}

// ---------- selectors ----------

export function useAuthUser(): AuthMe | null {
  return useAuthStore((s) => s.user);
}

export function useAuthRole(): Role | null {
  return useAuthStore((s) => s.user?.role ?? null);
}

export function useAuthTenantId(): string | null {
  return useAuthStore((s) => s.user?.tenant_id ?? null);
}

export function useAuthStatus(): AuthStatus {
  return useAuthStore((s) => s.status);
}
